import { useState, useEffect } from 'react';
import { FileText, Search, Download, Eye, Calendar, TrendingUp, AlertTriangle, CheckCircle, Filter, Loader, User } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface FinancialReport {
  id: string;
  user_id: string;
  periode_mois: string;
  statut_global: 'vert' | 'orange' | 'rouge';
  tresorerie_fin_mois: number;
  variation_tresorerie: number;
  resultat_mensuel_estime: number;
  charges_totales: number;
  note_synthese_statut: 'a_faire' | 'en_cours' | 'terminee';
  genere_le: string;
  genere_par: string;
  score_pilotage?: number;
  user_profiles: {
    first_name: string;
    last_name: string;
    company: string;
  };
}

export function ReportsManagement() {
  const [reports, setReports] = useState<FinancialReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [noteFilter, setNoteFilter] = useState<string>('all');

  useEffect(() => {
    fetchReports();
  }, []);

  async function fetchReports() {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('rapports_financiers')
        .select(`
          *,
          user_profiles!rapports_financiers_user_id_fkey (
            first_name,
            last_name,
            company
          )
        `)
        .order('periode_mois', { ascending: false });

      if (error) throw error;
      setReports(data || []);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  }

  async function downloadReportData(reportId: string) {
    try {
      const { data, error } = await supabase
        .from('rapports_financiers')
        .select('*')
        .eq('id', reportId)
        .single();

      if (error) throw error;

      const jsonBlob = new Blob([JSON.stringify(data, null, 2)], {
        type: 'application/json'
      });
      const url = URL.createObjectURL(jsonBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `rapport-${data.periode_mois}-${reportId.slice(0, 8)}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading report:', error);
      alert('Erreur lors du téléchargement');
    }
  }

  const filteredReports = reports.filter(report => {
    const profile = report.user_profiles;
    const clientName = profile?.company ||
      `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() ||
      'Inconnu';

    const matchesSearch = clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.periode_mois.includes(searchTerm);

    const matchesStatus = statusFilter === 'all' || report.statut_global === statusFilter;
    const matchesNote = noteFilter === 'all' || report.note_synthese_statut === noteFilter;

    return matchesSearch && matchesStatus && matchesNote;
  });

  const getStatusBadge = (status: string) => {
    const badges = {
      vert: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle, label: 'Sain' },
      orange: { bg: 'bg-orange-100', text: 'text-orange-800', icon: AlertTriangle, label: 'Attention' },
      rouge: { bg: 'bg-red-100', text: 'text-red-800', icon: AlertTriangle, label: 'Urgent' }
    };
    const badge = badges[status as keyof typeof badges];
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <Icon size={14} />
        {badge.label}
      </span>
    );
  };

  const getNoteStatusBadge = (status: string) => {
    const badges = {
      a_faire: { bg: 'bg-red-100', text: 'text-red-800', label: 'À faire' },
      en_cours: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'En cours' },
      terminee: { bg: 'bg-green-100', text: 'text-green-800', label: 'Terminée' }
    };
    const badge = badges[status as keyof typeof badges];
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  const stats = {
    total: reports.length,
    vert: reports.filter(r => r.statut_global === 'vert').length,
    orange: reports.filter(r => r.statut_global === 'orange').length,
    rouge: reports.filter(r => r.statut_global === 'rouge').length,
    notesPending: reports.filter(r => r.note_synthese_statut === 'a_faire').length,
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileText size={28} />
            Gestion des rapports financiers
          </h2>
          <p className="text-gray-600 mt-1">Tous les rapports générés pour l'ensemble des clients</p>
        </div>
      </div>

      <div className="grid grid-cols-5 gap-4 mb-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="text-sm text-gray-600 mb-1">Total rapports</div>
          <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="text-sm text-gray-600 mb-1">Situation saine</div>
          <div className="text-2xl font-bold text-green-600">{stats.vert}</div>
        </div>
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <div className="text-sm text-gray-600 mb-1">Attention</div>
          <div className="text-2xl font-bold text-orange-600">{stats.orange}</div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-sm text-gray-600 mb-1">Urgent</div>
          <div className="text-2xl font-bold text-red-600">{stats.rouge}</div>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="text-sm text-gray-600 mb-1">Notes à faire</div>
          <div className="text-2xl font-bold text-yellow-600">{stats.notesPending}</div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 mb-6 p-4">
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <Search size={16} />
              Rechercher
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Nom client ou période..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <Filter size={16} />
              Statut global
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Tous</option>
              <option value="vert">Sain</option>
              <option value="orange">Attention</option>
              <option value="rouge">Urgent</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <Filter size={16} />
              Statut note
            </label>
            <select
              value={noteFilter}
              onChange={(e) => setNoteFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Tous</option>
              <option value="a_faire">À faire</option>
              <option value="en_cours">En cours</option>
              <option value="terminee">Terminée</option>
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : filteredReports.length > 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Client</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Période</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Statut</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Trésorerie</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Résultat</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Score</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Note</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Généré le</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredReports.map((report) => {
                  const profile = report.user_profiles;
                  const clientName = profile?.company ||
                    `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() ||
                    'Inconnu';

                  return (
                    <tr key={report.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <User size={16} className="text-gray-400" />
                          <span className="font-medium text-gray-900">{clientName}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Calendar size={16} className="text-gray-400" />
                          <span className="text-gray-900">{report.periode_mois}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {getStatusBadge(report.statut_global)}
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-medium text-gray-900">
                          {report.tresorerie_fin_mois.toFixed(2)} €
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`font-medium ${
                          report.resultat_mensuel_estime >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {report.resultat_mensuel_estime.toFixed(2)} €
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {report.score_pilotage !== undefined ? (
                          <span className={`font-bold ${
                            report.score_pilotage >= 80 ? 'text-green-600' :
                            report.score_pilotage >= 60 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {report.score_pilotage}/100
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {getNoteStatusBadge(report.note_synthese_statut)}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-600">
                          {new Date(report.genere_le).toLocaleDateString('fr-FR')}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => window.open(`/admin/users/${report.user_id}`, '_blank')}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            title="Voir le client"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={() => downloadReportData(report.id)}
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                            title="Télécharger JSON"
                          >
                            <Download size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <FileText className="mx-auto text-gray-400 mb-4" size={48} />
          <p className="text-gray-600 text-lg">Aucun rapport trouvé</p>
          <p className="text-gray-500 text-sm mt-2">Aucun rapport ne correspond à vos critères de recherche</p>
        </div>
      )}
    </div>
  );
}
